import type { RecentlyUpdatedMacroQuery$variables } from '../__generated__/RecentlyUpdatedMacroQuery.graphql.ts';

import type { ContentType, RecentlyUpdatedMacroParams } from './recently-updated-types.ts';

export const DEFAULT_MAX = 15;

export const DEFAULT_TYPES: ContentType[] = [
	'page',
	'blogpost',
	'comment',
	'attachment',
	'whiteboard',
	'database',
	'embed',
	'folder',
];

const TYPE_ALIASES: Record<string, ContentType> = {
	news: 'blogpost',
	blog: 'blogpost',
	blogs: 'blogpost',
	'blog-post': 'blogpost',
	blogposts: 'blogpost',
	'blog-posts': 'blogpost',
	pages: 'page',
	comments: 'comment',
	attachments: 'attachment',
};

const SUPPORTED_TYPES = new Set(DEFAULT_TYPES);

function isContentType(type: string): type is ContentType {
	return SUPPORTED_TYPES.has(type as ContentType);
}

function normalizeType(rawType: string): ContentType | undefined {
	const normalized = TYPE_ALIASES[rawType] ?? rawType;
	return isContentType(normalized) ? normalized : undefined;
}

function parseCommaSeparatedValues(value: string | undefined): string[] {
	if (!value) {
		return [];
	}
	const parts = value.split(',');
	const result: string[] = [];
	for (const part of parts) {
		const trimmed = part.trim();
		if (trimmed.length > 0) {
			result.push(trimmed);
		}
	}
	return result;
}

type PrefixedValues = {
	included: string[];
	required: string[];
	excluded: string[];
};

function partitionPrefixedValues(values: string[]): PrefixedValues {
	const result: PrefixedValues = { included: [], required: [], excluded: [] };
	for (const value of values) {
		if (value.startsWith('+') && value.length > 1) {
			result.required.push(value.slice(1));
		} else if (value.startsWith('-') && value.length > 1) {
			result.excluded.push(value.slice(1));
		} else {
			result.included.push(value);
		}
	}
	return result;
}

const SPACE_SCOPE_TOKENS: Record<string, string> = {
	'@personal': 'space.type = personal',
	'@global': 'space.type != personal',
};

/**
 * CQL predicate for "every space this user can see", used for the `@all` / `*` space params.
 *
 * This MUST be an explicit predicate rather than an omitted space clause. Emitting no space
 * clause produces an unbounded CQL search that diverges from the legacy backend behaviour
 * (which resolved `@all` to the enumerated set of permitted spaces) and renders the macro as
 * an empty "No updates yet" state. See CONFCLOUD-84998.
 */
const ALL_SPACES_CLAUSE = 'space.type in (global, personal)';

/**
 * Resolves the macro `spaces` param into a single CQL space predicate.
 *
 * Always returns a predicate: `buildCql` must never produce a query without a space clause,
 * because an unbounded search does not match the legacy macro behaviour. When the param does
 * not resolve to anything concrete (no param and no host space, or tokens that resolve away)
 * the query falls back to all spaces rather than to an unbounded search.
 */
function resolveIncludedSpaces(spaceKeys: string[], currentSpaceKey: string): string | undefined {
	const resolvedSpaceKeys: string[] = [];
	const spaceScopeClauses = new Set<string>();
	let includeFavoriteSpaces = false;
	for (const spaceKey of spaceKeys) {
		if (spaceKey === '@all' || spaceKey === '*') {
			return ALL_SPACES_CLAUSE;
		}
		if (spaceKey === '@favorite' || spaceKey === '@favourite') {
			includeFavoriteSpaces = true;
			continue;
		}
		if (SPACE_SCOPE_TOKENS[spaceKey]) {
			spaceScopeClauses.add(SPACE_SCOPE_TOKENS[spaceKey]);
			continue;
		}

		const resolvedSpaceKey = spaceKey === '@self' ? currentSpaceKey : spaceKey;
		if (resolvedSpaceKey) {
			resolvedSpaceKeys.push(resolvedSpaceKey);
		}
	}

	const clauses: string[] = [];
	if (resolvedSpaceKeys.length === 1) {
		clauses.push(`space = "${resolvedSpaceKeys[0]}"`);
	} else if (resolvedSpaceKeys.length > 1) {
		clauses.push(`space in (${formatQuotedCqlValues(resolvedSpaceKeys)})`);
	}
	for (const spaceScopeClause of spaceScopeClauses) {
		clauses.push(spaceScopeClause);
	}
	if (includeFavoriteSpaces) {
		clauses.push('space in (favouriteSpaces())');
	}

	return clauses.length > 1 ? `(${clauses.join(' OR ')})` : clauses[0];
}

function resolveExcludedSpaces(spaceKeys: string[], currentSpaceKey: string): string[] {
	const excludedSpaceKeys: string[] = [];
	const clauses: string[] = [];
	for (const spaceKey of spaceKeys) {
		if (spaceKey === '@all' || spaceKey === '*') {
			clauses.push('space.type not in (global, personal)');
		} else if (spaceKey === '@self') {
			if (currentSpaceKey) {
				excludedSpaceKeys.push(currentSpaceKey);
			}
		} else if (spaceKey === '@personal') {
			clauses.push('space.type != personal');
		} else if (spaceKey === '@global') {
			clauses.push('space.type = personal');
		} else if (spaceKey === '@favorite' || spaceKey === '@favourite') {
			clauses.push('space not in (favouriteSpaces())');
		} else {
			excludedSpaceKeys.push(spaceKey);
		}
	}
	if (excludedSpaceKeys.length > 0) {
		clauses.unshift(`space not in (${formatQuotedCqlValues(excludedSpaceKeys)})`);
	}
	return clauses;
}

function resolveSpaceClause(
	spaces: string | undefined,
	currentSpaceKey: string,
	enablePrefixedFilters: boolean,
): string {
	const configuredSpaceKeys = parseCommaSeparatedValues(spaces);
	if (configuredSpaceKeys.length === 0) {
		return currentSpaceKey ? `space = "${currentSpaceKey}"` : ALL_SPACES_CLAUSE;
	}
	if (!enablePrefixedFilters) {
		return resolveIncludedSpaces(configuredSpaceKeys, currentSpaceKey) ?? ALL_SPACES_CLAUSE;
	}

	const { included, required, excluded } = partitionPrefixedValues(configuredSpaceKeys);
	const includeClause =
		resolveIncludedSpaces(required, currentSpaceKey) ??
		resolveIncludedSpaces(included, currentSpaceKey) ??
		ALL_SPACES_CLAUSE;
	const clauses = [includeClause, ...resolveExcludedSpaces(excluded, currentSpaceKey)];

	return clauses.length > 1 ? `(${clauses.join(' AND ')})` : clauses[0];
}

function formatQuotedCqlValues(values: string[]): string {
	return values.map((v) => `"${v}"`).join(', ');
}

export function resolveMaxItems(max: RecentlyUpdatedMacroParams['max']): number {
	const parsed = Number(max);
	return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : DEFAULT_MAX;
}

/**
 * Builds a CQL query string from the macro params, matching the behaviour of the
 * legacy RecentUpdateQueryParameters Java class which accepts:
 *   - followingUsers  (author param — comma-separated account IDs)
 *   - labels          (comma-separated label values)
 *   - spaceKeys       (comma-separated space keys)
 *   - contentTypes    (comma-separated types: page, blogpost, comment, attachment)
 *
 * @param params - Resolved macro parameters with string values.
 * @param spaceClause - The resolved CQL space clause.
 */
export function buildCql(
	params: RecentlyUpdatedMacroParams,
	spaceClause?: string,
	enablePrefixedFilters: boolean = false,
): string {
	const clauses: string[] = [];

	// Content types
	const typeSet = new Set<ContentType>();
	if (params.types) {
		for (const part of parseCommaSeparatedValues(String(params.types))) {
			const normalizedType = normalizeType(part.toLowerCase());
			if (normalizedType) {
				typeSet.add(normalizedType);
			}
		}
	} else {
		for (const t of DEFAULT_TYPES) {
			typeSet.add(t);
		}
	}
	if (typeSet.size > 0) {
		clauses.push(`type in (${[...typeSet].join(', ')})`);
	}

	// Space scope — defaults to the current space
	if (spaceClause) {
		clauses.push(spaceClause);
	}

	// Labels
	if (params.labels) {
		if (enablePrefixedFilters) {
			const { included, required, excluded } = partitionPrefixedValues(
				parseCommaSeparatedValues(String(params.labels)),
			);
			for (const requiredLabel of required) {
				clauses.push(`label = "${requiredLabel}"`);
			}
			if (included.length > 0) {
				clauses.push(`label in (${formatQuotedCqlValues(included)})`);
			}
			if (excluded.length > 0) {
				clauses.push(`label not in (${formatQuotedCqlValues(excluded)})`);
			}
		} else {
			clauses.push(
				`label in (${formatQuotedCqlValues(parseCommaSeparatedValues(String(params.labels)))})`,
			);
		}
	}

	// Following users / contributors (author param — comma-separated account IDs)
	if (params.author) {
		const users = String(params.author)
			.split(',')
			.map((u) => `"${u.trim()}"`)
			.join(', ');
		clauses.push(`contributor in (${users})`);
	}

	return `${clauses.join(' AND ')} order by lastmodified desc`;
}

export function buildRecentlyUpdatedQueryVariables(
	params: RecentlyUpdatedMacroParams,
	currentSpaceKey: string,
	after: string = '',
	enablePrefixedFilters: boolean = false,
): RecentlyUpdatedMacroQuery$variables {
	const maxItems = resolveMaxItems(params.max);
	const spaceClause = resolveSpaceClause(params.spaces, currentSpaceKey, enablePrefixedFilters);

	return {
		cql: buildCql(params, spaceClause, enablePrefixedFilters),
		cqlcontext: '{"contentStatuses":["draft", "current"]}',
		first: maxItems,
		after,
	};
}
